<template>
  <div class="ip-input-container" data-testid="ip-input-container">
    <a-form @submit.prevent="handleLookup" data-testid="ip-lookup-form">
      <a-form-item
        :validate-status="validationStatus"
        :help="validationMessage"
        style="margin-bottom: 0;"
        data-testid="ip-form-item"
      >
        <a-input-group compact size="large" class="input-group">
          <a-input
            v-model:value="ipAddress"
            placeholder="Enter IP address (e.g., 172.67.188.91 or 2606:4700::)"
            size="large"
            :disabled="loading"
            @blur="validateInput"
            @input="validateInput"
            @keyup.enter="handleLookup"
            class="ip-input"
            data-testid="ip-input-field"
          >
            <template #prefix>
              <global-outlined />
            </template>
          </a-input>
          <a-button
            type="primary"
            html-type="submit"
            :loading="loading"
            :disabled="!isValid"
            size="large"
            class="lookup-button"
            data-testid="ip-lookup-button"
          >
            <template #icon>
              <search-outlined />
            </template>
            Lookup
          </a-button>
        </a-input-group>
      </a-form-item>
    </a-form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { GlobalOutlined, SearchOutlined } from '@ant-design/icons-vue';

interface Props {
  loading?: boolean;
}

interface Emits {
  (e: 'lookup', ipAddress: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const emit = defineEmits<Emits>();

const ipAddress = ref('');
const isValid = ref(false);
const errorMessage = ref('');

const validationStatus = computed(() => {
  if (ipAddress.value === '') return '';
  return isValid.value ? 'success' : 'error';
});

const validationMessage = computed(() => {
  if (ipAddress.value === '') return '';
  return isValid.value ? '' : errorMessage.value;
});

const validateInput = () => {
  const value = ipAddress.value.trim();

  if (value === '') {
    isValid.value = false;
    errorMessage.value = '';
    return;
  }

  // IPv4 validation
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(%)?$/;
  // IPv6 validation (basic)
  const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(%)?$/;

  if (ipv4Regex.test(value)) {
    // Check each octet is 0-255
    const octets = value.replace('%', '').split('.');
    const validOctets = octets.every((octet: string) => {
      const num = parseInt(octet, 10);
      return num >= 0 && num <= 255;
    });

    if (validOctets) {
      isValid.value = true;
      errorMessage.value = '';
    } else {
      isValid.value = false;
      errorMessage.value = 'Invalid IPv4 address';
    }
  } else if (ipv6Regex.test(value)) {
    isValid.value = true;
    errorMessage.value = '';
  } else {
    isValid.value = false;
    errorMessage.value = 'Invalid IP address format';
  }
};

const handleLookup = () => {
  const value = ipAddress.value.trim();

  if (value === '') {
    isValid.value = false;
    errorMessage.value = 'Please enter an IP address';
    return;
  }

  validateInput();

  if (isValid.value) {
    emit('lookup', value);
  }
};
</script>

<style scoped lang="scss">
.ip-input-container {
  max-width: 100%;
  
  .input-group {
    display: flex;
    width: 100%;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
    
    .ip-input {
      flex: 1;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      
      @media (max-width: 768px) {
        border-radius: 8px !important;
      }
      
      :deep(.ant-input) {
        font-size: 16px;
        font-weight: 500;
        padding: 12px 16px;

        @media (max-width: 768px) {
          padding: 12px 32px !important;
        }
      }
      
      :deep(.ant-input-prefix) {
        margin-right: 12px;
        font-size: 18px;
      }
    }
    
    .lookup-button {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      font-weight: 600;
      font-size: 16px;
      height: 64px;
      line-height: initial;
      padding: 0 32px;
      white-space: nowrap;
      box-shadow: none;
      
      @media (max-width: 768px) {
        border-radius: 8px !important;
        width: 100%;
      }
      
      &:hover, &:focus {
        box-shadow: none;
      }
    }
  }
  
  :deep(.ant-form-item-explain-error) {
    margin-top: 8px;
    font-weight: 500;
  }
}
</style>
